/**
 * executable-strategy-code.ts
 *
 * Helper to resolve the best executable code string for a user strategy before
 * passing it to the Web Worker compiler (compileStrategy).
 *
 * Every strategy stored in the database has two code fields:
 *   - `code`         — original TypeScript source written by the LLM / user.
 *   - `compiledCode` — esbuild-compiled, type-annotation-free JavaScript.
 *
 * The Worker's compileStrategy() strips type annotations with hand-written
 * regexes that miss some TS constructs (e.g. inline object type annotations like
 * `const cfg: { period: number } = ...`), causing "Unexpected token ':'" at
 * runtime. Feeding compiledCode through the compiler sidesteps this entirely —
 * the stripping becomes a harmless no-op because the code is already clean JS.
 *
 * Rule 11 (no silent fallbacks): the fallback to raw TypeScript source MUST be
 * explicit and logged so operators know which strategies have not been compiled.
 */

/**
 * Return the best executable code string for the given strategy.
 *
 * @param strategy - Object with `code` (raw TS source), optional `compiledCode`
 *                   (esbuild-compiled JS), and optional `name` for diagnostics.
 * @returns The esbuild-compiled JS if available, otherwise the raw TS source.
 * @throws  If both `compiledCode` and `code` are absent or whitespace-only.
 */
export function executableStrategyCode(strategy: {
  name?: string;
  code: string;
  compiledCode?: string;
}): string {
  const label = strategy.name ?? 'unknown';

  // Prefer esbuild-compiled JS: type annotations are already stripped, so the
  // Worker's regex-based stripTypeAnnotations becomes a safe no-op.
  if (strategy.compiledCode && strategy.compiledCode.trim().length > 0) {
    return strategy.compiledCode;
  }

  // Fallback to raw TypeScript source — explicitly warn so this is never silent.
  // Rule 11: explicit, logged fallback rather than a silent default.
  if (strategy.code && strategy.code.trim().length > 0) {
    console.warn(
      `[executableStrategyCode] Strategy "${label}" has no compiled code — ` +
        `falling back to raw TypeScript source. ` +
        `The regex-based type-annotation stripper may fail for complex TS constructs ` +
        `(e.g. inline object types, function-type annotations). ` +
        `Re-save the strategy to regenerate its compiled_code.`,
    );
    return strategy.code;
  }

  // Both are empty/absent — this is an unrecoverable error (Rule 11: loud failure).
  throw new Error(
    `[executableStrategyCode] Strategy "${label}" has no executable code: ` +
      `both compiledCode and code are empty or whitespace. ` +
      `The strategy may be corrupted — re-save it via the connected agent (MCP).`,
  );
}
